import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  Shield,
  FileText,
  Cookie,
  AlertTriangle,
  Scale,
  Send,
  CheckCircle2,
  Copy,
  Printer,
  ChevronRight,
  Search,
  ExternalLink,
  ShieldCheck,
  Mail,
  Building2,
  User,
  PenTool,
  Clock,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

const LEGAL_PAGES = [
  { id: "terminos", label: "Términos de uso", icon: FileText, desc: "Condiciones de acceso, uso y normas de la plataforma" },
  { id: "privacidad", label: "Política de privacidad", icon: Shield, desc: "Protección de datos personales, RGPD y tus derechos" },
  { id: "cookies", label: "Política de cookies", icon: Cookie, desc: "Uso de cookies propias, analítica y publicidad" },
  { id: "derechos", label: "Aviso de derechos de autor (DMCA)", icon: Scale, desc: "Safe Harbor, puerto seguro y propiedad intelectual" },
  { id: "formulario-dmca", label: "Formulario de Reclamación DMCA", icon: AlertTriangle, desc: "Procedimiento formal y radicación de expediente legal", highlight: true },
  { id: "descargo", label: "Descargo de responsabilidad", icon: AlertTriangle, desc: "Declaración de transparencia, divulgación cultural y enlaces" },
];

export default function Legal() {
  const { page } = useParams();
  const [searchParams] = useSearchParams();
  const activePage = LEGAL_PAGES.find((p) => p.id === page) ? page : "terminos";

  // Search in legal content
  const [filterText, setFilterText] = useState("");

  // DMCA Form state
  const [dmcaForm, setDmcaForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    organization: "",
    infringing_url_or_title: "",
    original_work_evidence: "",
    description: "",
    good_faith: false,
    accuracy_oath: false,
    signature: "",
  });

  const [submittingDmca, setSubmittingDmca] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState(null);

  // Pre-fill DMCA form if query params are present (e.g. from TitleDetail report button)
  useEffect(() => {
    const titleParam = searchParams.get("title");
    const urlParam = searchParams.get("url");
    if (titleParam || urlParam) {
      setDmcaForm((prev) => ({
        ...prev,
        infringing_url_or_title: [titleParam ? `Título: ${titleParam}` : "", urlParam ? `URL: ${urlParam}` : ""]
          .filter(Boolean)
          .join(" | "),
      }));
    }
  }, [searchParams]);

  const handlePrint = () => {
    window.print();
  };

  const handleDmcaSubmit = async (e) => {
    e.preventDefault();
    if (!dmcaForm.full_name || !dmcaForm.email || !dmcaForm.infringing_url_or_title || !dmcaForm.description || !dmcaForm.signature) {
      toast.error("Por favor completa todos los campos requeridos del formulario");
      return;
    }
    if (!dmcaForm.good_faith || !dmcaForm.accuracy_oath) {
      toast.error("Debes aceptar las declaraciones bajo pena de perjurio para que la solicitud sea jurídicamente válida.");
      return;
    }

    setSubmittingDmca(true);
    try {
      const res = await api.post("/legal/dmca-claim", dmcaForm);
      if (res.data && res.data.ticket_id) {
        setSubmittedTicket(res.data.ticket_id);
        toast.success(`Reclamación radicada con éxito. Ticket: ${res.data.ticket_id}`);
      } else {
        toast.success("Reclamación radicada exitosamente.");
        setSubmittedTicket("DMCA-PROCESSED");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al radicar la reclamación. Por favor intenta de nuevo.");
    } finally {
      setSubmittingDmca(false);
    }
  };

  const copyTicket = () => {
    if (!submittedTicket) return;
    navigator.clipboard.writeText(submittedTicket);
    toast.success("Número de expediente copiado al portapapeles");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-[#E11D48] selection:text-white" data-testid="legal-page">
      <Helmet>
        <title>{`${LEGAL_PAGES.find((p) => p.id === activePage)?.label || "Información Legal"} — La Caja LGBT`}</title>
        <meta name="description" content="Términos y condiciones, política de privacidad, cookies y procedimiento de derechos de autor DMCA de La Caja LGBT." />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Navbar />

      <main className="flex-1 pt-28 pb-20 px-4 sm:px-6 md:px-12 max-w-7xl mx-auto w-full">
        {/* Header Banner */}
        <div className="mb-10 pb-6 border-b border-white/10 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#E11D48] mb-2">
              <ShieldCheck className="w-4 h-4" />
              <span>Marco Jurídico & Transparencia</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight text-white">
              Centro Legal y de Cumplimiento
            </h1>
            <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
              Garantizamos la seguridad de nuestra comunidad, el cumplimiento de normativas internacionales de privacidad (RGPD / CCPA) y el estricto respeto a la propiedad intelectual (DMCA / DSA).
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-zinc-900 border border-zinc-800 text-xs font-medium text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors"
              title="Imprimir documento actual"
            >
              <Printer className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Imprimir documento</span>
            </button>
            <Link
              to="/legal/formulario-dmca"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E11D48] text-white text-xs font-semibold hover:bg-[#be123c] transition-colors shadow-lg shadow-[#E11D48]/20"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Radicar Reclamación</span>
            </Link>
          </div>
        </div>

        {/* Layout Grid: Sidebar Navigation + Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Navigation Sidebar */}
          <aside className="lg:col-span-4 space-y-4">
            <div className="bg-zinc-950/80 border border-zinc-800/80 rounded-2xl p-4 sticky top-28 backdrop-blur-md">
              <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-400 mb-3 px-2">
                Documentos Legales
              </p>
              <nav className="space-y-1.5" aria-label="Navegación legal">
                {LEGAL_PAGES.map((item) => {
                  const Icon = item.icon;
                  const isActive = activePage === item.id;
                  return (
                    <Link
                      key={item.id}
                      to={`/legal/${item.id}`}
                      data-testid={`legal-nav-${item.id}`}
                      className={`flex items-start gap-3 p-3 rounded-xl transition-all duration-200 group ${
                        isActive
                          ? "bg-[#E11D48]/15 border border-[#E11D48]/40 text-white font-medium shadow-sm"
                          : "text-zinc-400 hover:text-zinc-200 hover:bg-white/5 border border-transparent"
                      }`}
                    >
                      <div
                        className={`p-2 rounded-lg shrink-0 mt-0.5 ${
                          isActive
                            ? "bg-[#E11D48] text-white"
                            : item.highlight
                            ? "bg-amber-500/20 text-amber-400 group-hover:bg-amber-500/30"
                            : "bg-zinc-900 text-zinc-400 group-hover:text-zinc-200"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm leading-snug ${isActive ? "text-white font-semibold" : "group-hover:text-white"}`}>
                            {item.label}
                          </span>
                          {isActive && <ChevronRight className="w-4 h-4 text-[#E11D48] shrink-0" />}
                        </div>
                        <p className="text-[11px] text-zinc-500 line-clamp-1 mt-0.5">{item.desc}</p>
                      </div>
                    </Link>
                  );
                })}
              </nav>

              {/* Direct Legal Contact Card */}
              <div className="mt-6 pt-5 border-t border-zinc-850 bg-black/40 -mx-4 -mb-4 p-4 rounded-b-2xl">
                <div className="flex items-center gap-2 text-xs font-semibold text-zinc-300 mb-1.5">
                  <Mail className="w-3.5 h-3.5 text-[#E11D48]" />
                  <span>Contacto Departamento Legal</span>
                </div>
                <p className="text-[11px] text-zinc-400 leading-relaxed">
                  Para requerimientos de juzgados, entidades regulatorias o titulares de propiedad intelectual:
                </p>
                <div className="mt-2 text-xs space-y-1 font-mono text-zinc-300">
                  <p>⚖️ <span className="text-zinc-400">DMCA:</span> copyright@lacajalgbt.com</p>
                  <p>🔒 <span className="text-zinc-400">Privacidad:</span> privacidad@lacajalgbt.com</p>
                  <p>🏛️ <span className="text-zinc-400">General:</span> legal@lacajalgbt.com</p>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Legal Content Container */}
          <section className="lg:col-span-8 bg-zinc-950/60 border border-zinc-800/80 rounded-2xl p-6 sm:p-10 backdrop-blur-sm">
            {/* Search within document */}
            {activePage !== "formulario-dmca" && (
              <div className="mb-8 pb-6 border-b border-zinc-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Clock className="w-3.5 h-3.5 text-zinc-500" />
                  <span>Última revisión legal: <strong>14 de Agosto de 2026</strong></span>
                  <span className="text-zinc-600">·</span>
                  <span className="text-emerald-400 font-medium">Vigente y aplicable</span>
                </div>

                <div className="relative w-full sm:w-64">
                  <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    placeholder="Filtrar cláusula o término..."
                    className="w-full bg-zinc-900/90 border border-zinc-800 rounded-xl pl-9 pr-3 py-1.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Content Switcher */}
            {activePage === "terminos" && <TermsOfServiceContent filterText={filterText} />}
            {activePage === "privacidad" && <PrivacyPolicyContent filterText={filterText} />}
            {activePage === "cookies" && <CookiePolicyContent filterText={filterText} />}
            {activePage === "derechos" && <CopyrightDmcaContent filterText={filterText} />}
            {activePage === "formulario-dmca" && (
              <DmcaFormSection
                dmcaForm={dmcaForm}
                setDmcaForm={setDmcaForm}
                handleSubmit={handleDmcaSubmit}
                submitting={submittingDmca}
                submittedTicket={submittedTicket}
                copyTicket={copyTicket}
                setSubmittedTicket={setSubmittedTicket}
              />
            )}
            {activePage === "descargo" && <DisclaimerContent filterText={filterText} />}

            {/* Bottom Cross-links Navigation */}
            <div className="mt-12 pt-8 border-t border-zinc-800/80 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-400">
              <div className="flex flex-wrap gap-4">
                <Link to="/legal/terminos" className="hover:text-white transition-colors">Términos</Link>
                <Link to="/legal/privacidad" className="hover:text-white transition-colors">Privacidad</Link>
                <Link to="/legal/cookies" className="hover:text-white transition-colors">Cookies</Link>
                <Link to="/legal/derechos" className="hover:text-white transition-colors">DMCA</Link>
                <Link to="/legal/formulario-dmca" className="text-amber-400 hover:underline">Formulario Retirada</Link>
              </div>
              <Link to="/" className="text-[#E11D48] hover:underline font-medium">
                ← Volver al Catálogo Principal
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

// -------------------------------------------------------------
// 1. TÉRMINOS Y CONDICIONES GENERALES DE USO (TERMS OF SERVICE)
// -------------------------------------------------------------
function TermsOfServiceContent({ filterText }) {
  const clauses = [
    {
      num: "1. Identificación y Ámbito de Aplicación",
      body: `El presente documento establece los Términos y Condiciones de Uso que rigen el acceso, navegación y utilización de la plataforma web y servicios digitales de "La Caja LGBT" (en adelante, "el Sitio Web", "la Plataforma" o "el Servicio"). Al acceder, navegar o utilizar cualquier parte de este sitio web, el usuario declara haber leído, comprendido y aceptado sin reservas estas condiciones. Si no está de acuerdo con alguno de los términos estipulados, deberá abstenerse de utilizar la Plataforma.`
    },
    {
      num: "2. Naturaleza del Servicio: Directorio Cultural e Informativo",
      body: `La Caja LGBT opera estrictamente como un directorio cultural, guía de descubrimiento cinematográfico, hemeroteca y revista digital especializada en cine, series, festivales y creadores de temática LGBTQ+. La Plataforma NO constituye un servicio de alojamiento (hosting), subida, distribución ni transmisión directa de obras audiovisuales completas protegidas. Los tráilers, avances y clips presentes en el sitio son reproducidos mediante tecnologías de incrustación (embedding) autorizadas desde servidores oficiales de terceros (tales como YouTube, Vimeo y distribuidoras certificadas), quedando alojados íntegramente en sus respectivas fuentes.`
    },
    {
      num: "3. Capacidad Legal y Clasificación de Contenidos (+18)",
      body: `El acceso general al catálogo está orientado a personas con capacidad jurídica para obligarse conforme a la legislación de su país de residencia. Determinadas obras cinematográficas, documentales o artículos periodísticos pueden abordar temáticas complejas, adultas o con clasificaciones orientativas por edad (+16 / +18). Es responsabilidad exclusiva de los tutores legales supervisar el consumo de contenidos por parte de personas menores de edad.`
    },
    {
      num: "4. Registro de Usuarios, Seguridad de Cuentas y Contraseñas",
      body: `El registro de una cuenta de usuario es totalmente voluntario y habilita funciones personalizadas como la gestión de 'Mi Lista', calificaciones cinematográficas, seguimiento de progreso y comentarios comunitarios. El usuario es el único responsable de la veracidad de los datos aportados y de la estricta confidencialidad de sus credenciales de acceso. Queda prohibida la cesión, venta o transferencia de cuentas a terceros. La Plataforma se reserva la potestad de suspender o cancelar unilateralmente cuentas que empleen correos falsos, nombres ofensivos o que incurran en actividades no autorizadas.`
    },
    {
      num: "5. Normas de Conducta de la Comunidad y Moderación",
      body: `Los espacios de debate y comentarios en artículos y fichas cinematográficas están destinados a fomentar el diálogo constructivo y la visibilidad de la cultura LGBTQ+. Queda terminantemente prohibido publicar mensajes que constituyan discursos de odio, incitación a la violencia, discriminación, acoso, difamación, spam publicitario, enlaces a malware o contenidos contrarios a la ley. Nos reservamos el derecho de moderar, editar o suprimir cualquier comentario que vulnere estas directrices sin previo aviso.`
    },
    {
      num: "6. Propiedad Intelectual de Terceros y Metadatos",
      body: `Los nombres comerciales, logotipos, carátulas, pósteres, sinopsis y materiales promocionales de las obras reseñadas pertenecen a sus respectivos autores, productores, distribuidoras y titulares legales. La inclusión de estos elementos en La Caja LGBT se realiza exclusivamente con fines informativos, de cita, reseña crítica y difusión cultural bajo la doctrina de uso legítimo (Fair Use / Uso Justo) y las directivas internacionales de comercio electrónico. La información técnica y metadatos se sincronizan mediante interfaces de programación públicas (APIs) autorizadas de bases de datos cinematográficas (TMDb).`
    },
    {
      num: "7. Prohibición de Uso Indebido y Protección Técnica",
      body: `Queda expresamente prohibido: (a) Realizar ingeniería inversa, descompilar o intentar extraer el código fuente de la Plataforma; (b) Emplear sistemas automatizados (bots, scrapers, arañas web) para extraer masivamente datos o saturar los servidores; (c) Eludir, desactivar o manipular cualquier mecanismo de seguridad o control de acceso; (d) Introducir virus informáticos o scripts maliciosos.`
    },
    {
      num: "8. Publicidad de Terceros y Enlaces Salientes (Google Ads)",
      body: `La Plataforma puede exhibir espacios publicitarios servidos por redes publicitarias de terceros (incluido Google AdSense) y enlaces hacia sitios web externos. La Caja LGBT no ejerce control editorial sobre los sitios web enlazados ni sobre los productos o servicios ofrecidos por terceros, declinando toda responsabilidad por pérdidas o daños derivados del acceso a dichos sitios ajenos.`
    },
    {
      num: "9. Exclusión de Garantías y Limitación de Responsabilidad",
      body: `El Servicio se proporciona 'tal cual' (as is) y 'según disponibilidad' (as available), sin garantías explícitas o implícitas sobre la continuidad ininterrumpida o la ausencia total de errores técnicos en servidores externos. En la máxima medida permitida por la legislación aplicable, La Caja LGBT no responderá por daños indirectos, lucro cesante o pérdidas de datos resultantes del uso o imposibilidad de uso del sitio.`
    },
    {
      num: "10. Modificaciones del Servicio y Legislación Aplicable",
      body: `Nos reservamos el derecho de actualizar, modificar o sustituir estos Términos de Uso en cualquier momento. Cualquier cambio significativo entrará en vigor desde su publicación en esta página. El uso continuado del servicio tras la publicación de revisiones constituirá la plena aceptación de las mismas. Para la resolución de controversias se aplicarán los principios de resolución amistosa y las leyes generales del comercio electrónico internacional.`
    }
  ];

  const filtered = clauses.filter(
    (c) =>
      !filterText ||
      c.num.toLowerCase().includes(filterText.toLowerCase()) ||
      c.body.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div className="space-y-8" data-testid="legal-content-terms">
      <div className="border-b border-zinc-800 pb-6">
        <span className="text-xs uppercase tracking-widest text-[#E11D48] font-bold">Documento Contractual</span>
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white mt-1">Términos y Condiciones Generales de Uso</h2>
        <p className="text-zinc-400 text-xs sm:text-sm mt-2 leading-relaxed">
          Reglas vinculantes de acceso, derechos de los usuarios y condiciones operativas de la plataforma cultural La Caja LGBT.
        </p>
      </div>

      <div className="space-y-6">
        {filtered.map((item) => (
          <article key={item.num} className="bg-zinc-900/50 border border-zinc-800/60 p-5 sm:p-6 rounded-xl hover:border-zinc-700 transition-colors">
            <h3 className="text-base sm:text-lg font-semibold text-white mb-2.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#E11D48]" />
              <span>{item.num}</span>
            </h3>
            <p className="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify sm:text-left">
              {item.body}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// 2. POLÍTICA DE PRIVACIDAD Y PROTECCIÓN DE DATOS (RGPD / CCPA)
// -------------------------------------------------------------
function PrivacyPolicyContent({ filterText }) {
  const sections = [
    {
      title: "1. Responsable del Tratamiento de sus Datos",
      content: `La Caja LGBT es el responsable del tratamiento de los datos personales recabados a través de esta plataforma digital. Para cualquier cuestión relativa a la protección de sus datos personales o al ejercicio de sus derechos legales, puede contactar directamente con nuestro Delegado de Privacidad a través de la dirección electrónica: privacidad@lacajalgbt.com.`
    },
    {
      title: "2. Principios Rectores del Tratamiento (RGPD / CCPA)",
      content: `El tratamiento de sus datos se rige estrictamente por los principios de: (a) Licitud, lealtad y transparencia; (b) Limitación de la finalidad (únicamente para los fines autorizados); (c) Minimización de datos (sólo los estrictamente necesarios); (d) Exactitud y actualización; (e) Limitación del plazo de conservación; y (f) Integridad y confidencialidad mediante cifrado en reposo y en tránsito (SSL/TLS).`
    },
    {
      title: "3. Categorías de Datos que Recopilamos",
      content: `Recopilamos únicamente las siguientes categorías de datos:
      • Datos de Registro e Identificación: Nombre de usuario público, dirección de correo electrónico, contraseña (cifrada con algoritmo bcrypt unidireccional) y opcionalmente número telefónico de contacto si usted lo proporciona para verificaciones de seguridad.
      • Datos de Actividad en la Plataforma: Historial de títulos añadidos a 'Mi Lista', calificaciones otorgadas (1 a 5 estrellas), registro de reproducción para reanudar el contenido ('Continuar viendo') y comentarios publicados en artículos de la revista.
      • Datos Técnicos y de Navegación: Dirección IP aproximada, tipo de navegador, sistema operativo y fecha/hora de conexión con fines estrictos de prevención de fraude, rate limiting contra ataques DDoS y métricas agregadas de rendimiento.`
    },
    {
      title: "4. Base Jurídica y Finalidades del Tratamiento",
      content: `Tratamos sus datos en base a:
      • Ejecución de la relación contractual: Para permitir el inicio de sesión, sincronización de Mi Lista y funcionamiento de su cuenta.
      • Consentimiento expreso: Para el envío de boletines informativos (Newsletter) previa suscripción voluntaria y para la gestión de cookies publicitarias.
      • Interés legítimo y seguridad: Para proteger la infraestructura frente a accesos indebidos, spam y moderación comunitaria.`
    },
    {
      title: "5. Cesión de Datos a Terceros y Proveedores Tecnológicos",
      content: `No vendemos, alquilamos ni comercializamos sus datos personales bajo ninguna circunstancia. Sus datos únicamente se comunican a proveedores tecnológicos indispensables para el funcionamiento del servicio bajo estrictos acuerdos de confidencialidad:
      • Proveedores de Infraestructura en la Nube: Google Cloud Platform / Firebase (servidores seguros en centros de datos con certificación ISO 27001).
      • Redes Publicitarias: Google AdSense (únicamente si usted ha otorgado consentimiento para cookies publicitarias).
      • Pasarelas de Envío de Correo Transaccional: Resend / Amazon SES / Nodemailer (exclusivamente para remitirle correos del boletín o confirmaciones de cuenta solicitadas).`
    },
    {
      title: "6. Sus Derechos Legales (ARCO-POL)",
      content: `Conforme a la normativa internacional (Reglamento General de Protección de Datos de la UE - RGPD y leyes análogas en América Latina y EE.UU.), usted tiene derecho en cualquier momento a:
      1. Acceso: Conocer qué datos personales conservamos sobre usted.
      2. Rectificación: Solicitar la corrección de datos inexactos.
      3. Supresión (Derecho al Olvido): Solicitar el borrado definitivo de su cuenta y todos sus datos asociados.
      4. Oposición y Limitación: Oponerse al tratamiento o solicitar la restricción del mismo.
      5. Portabilidad: Recibir sus datos en formato estructurado e interoperable (JSON/CSV).
      Para ejercer cualquiera de estos derechos, basta con enviar una solicitud por correo a privacidad@lacajalgbt.com indicando su correo de registro.`
    },
    {
      title: "7. Conservación de los Datos",
      content: `Los datos vinculados a su cuenta se conservarán mientras mantenga su perfil activo. Si decide eliminar su cuenta o darse de baja del boletín, sus datos serán suprimidos de forma segura e irreversible de nuestras bases de datos en un plazo máximo de 30 días naturales.`
    }
  ];

  const filtered = sections.filter(
    (s) =>
      !filterText ||
      s.title.toLowerCase().includes(filterText.toLowerCase()) ||
      s.content.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div className="space-y-8" data-testid="legal-content-privacy">
      <div className="border-b border-zinc-800 pb-6">
        <span className="text-xs uppercase tracking-widest text-[#E11D48] font-bold">Privacidad y Protección de Datos</span>
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white mt-1">Política de Privacidad Integral</h2>
        <p className="text-zinc-400 text-xs sm:text-sm mt-2 leading-relaxed">
          Detalle exhaustivo de cómo tratamos, protegemos y garantizamos sus derechos sobre sus datos personales conforme a los más altos estándares internacionales (RGPD / CCPA).
        </p>
      </div>

      <div className="space-y-6">
        {filtered.map((item) => (
          <article key={item.title} className="bg-zinc-900/50 border border-zinc-800/60 p-5 sm:p-6 rounded-xl hover:border-zinc-700 transition-colors">
            <h3 className="text-base sm:text-lg font-semibold text-white mb-2.5 flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#E11D48]" />
              <span>{item.title}</span>
            </h3>
            <div className="text-zinc-300 text-xs sm:text-sm leading-relaxed whitespace-pre-line text-justify sm:text-left">
              {item.content}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// 3. POLÍTICA DE COOKIES Y RASTREO (COOKIE POLICY)
// -------------------------------------------------------------
function CookiePolicyContent({ filterText }) {
  const sections = [
    {
      title: "¿Qué son las cookies y para qué se utilizan?",
      desc: `Una cookie es un pequeño archivo de texto que un sitio web descarga en su dispositivo (ordenador, smartphone o tableta) cuando usted navega por él. Las cookies permiten a la plataforma recordar sus preferencias de navegación, mantener activa su sesión de usuario y analizar el rendimiento del sitio para ofrecer una experiencia fluida.`
    },
    {
      title: "Tipos de cookies que utiliza La Caja LGBT",
      table: [
        {
          tipo: "Cookies Técnicas y Esenciales",
          finalidad: "Imprescindibles para el funcionamiento de la web: autenticación de usuario con tokens JWT protegidos (HttpOnly), mantenimiento de sesión y guardado de su consentimiento de cookies.",
          duracion: "Sesión / 7 días",
          proveedor: "Propias (La Caja LGBT)"
        },
        {
          tipo: "Cookies de Personalización y Preferencias",
          finalidad: "Almacenan sus elecciones de interfaz: idioma seleccionado (Español, Inglés, Francés) y preferencias de visualización local (LocalStorage).",
          duracion: "Permanente (Local)",
          proveedor: "Propias"
        },
        {
          tipo: "Cookies de Publicidad y Terceros (Google AdSense)",
          finalidad: "Permiten mostrar anuncios contextuales y medir la eficacia de las campañas publicitarias sin almacenar datos directamente identificables.",
          duracion: "Variable (hasta 13 meses)",
          proveedor: "Google LLC / Red AdSense"
        }
      ]
    },
    {
      title: "¿Cómo configurar o revocar el consentimiento de cookies?",
      desc: `Usted puede en cualquier momento aceptar o rechazar las cookies no esenciales a través de nuestro banner de cookies en la parte inferior de la pantalla. Asimismo, todos los navegadores modernos permiten bloquear o eliminar cookies en cualquier momento desde sus menús de configuración:
      • Google Chrome: Configuración > Privacidad y seguridad > Cookies y otros datos de sitios.
      • Mozilla Firefox: Opciones > Privacidad y Seguridad > Cookies y datos del sitio.
      • Apple Safari: Preferencias > Privacidad > Bloquear todas las cookies.
      • Microsoft Edge: Configuración > Privacidad, búsqueda y servicios > Cookies.`
    }
  ];

  return (
    <div className="space-y-8" data-testid="legal-content-cookies">
      <div className="border-b border-zinc-800 pb-6">
        <span className="text-xs uppercase tracking-widest text-[#E11D48] font-bold">Transparencia de Rastreo</span>
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white mt-1">Política de Cookies</h2>
        <p className="text-zinc-400 text-xs sm:text-sm mt-2 leading-relaxed">
          Información clara sobre las tecnologías de almacenamiento local y cookies empleadas en nuestra plataforma.
        </p>
      </div>

      <div className="space-y-6">
        <div className="bg-zinc-900/50 border border-zinc-800/60 p-5 sm:p-6 rounded-xl">
          <h3 className="text-base font-semibold text-white mb-2">{sections[0].title}</h3>
          <p className="text-zinc-300 text-xs sm:text-sm leading-relaxed">{sections[0].desc}</p>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800/60 p-5 sm:p-6 rounded-xl">
          <h3 className="text-base font-semibold text-white mb-4">{sections[1].title}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-zinc-800 text-zinc-400 uppercase text-[10px] tracking-wider">
                  <th className="pb-3 pr-4">Categoría</th>
                  <th className="pb-3 pr-4">Finalidad</th>
                  <th className="pb-3 pr-4">Duración</th>
                  <th className="pb-3">Proveedor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60 text-zinc-300">
                {sections[1].table.map((row, idx) => (
                  <tr key={idx} className="hover:bg-white/5 transition-colors">
                    <td className="py-3 pr-4 font-semibold text-white">{row.tipo}</td>
                    <td className="py-3 pr-4">{row.finalidad}</td>
                    <td className="py-3 pr-4 whitespace-nowrap text-zinc-400">{row.duracion}</td>
                    <td className="py-3 font-mono text-[11px] text-zinc-400">{row.proveedor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800/60 p-5 sm:p-6 rounded-xl">
          <h3 className="text-base font-semibold text-white mb-2">{sections[2].title}</h3>
          <p className="text-zinc-300 text-xs sm:text-sm leading-relaxed whitespace-pre-line">{sections[2].desc}</p>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// 4. AVISO DE DERECHOS DE AUTOR Y PUERTO SEGURO (DMCA NOTICE)
// -------------------------------------------------------------
function CopyrightDmcaContent({ filterText }) {
  const points = [
    {
      title: "1. Declaración de Safe Harbor (Puerto Seguro)",
      content: `La Caja LGBT opera estrictamente como prestador de servicios de intermediación de la sociedad de la información y catálogo cultural, acogido a las disposiciones de puerto seguro del Digital Millennium Copyright Act (DMCA, 17 U.S.C. § 512), la Directiva de Comercio Electrónico de la Unión Europea (2000/31/CE) y el Reglamento de Servicios Digitales (Digital Services Act - DSA). La Plataforma NO aloja en sus propios servidores archivos maestros ni copias reproducibles de películas o series comerciales protegidas.`
    },
    {
      title: "2. Origen de la Información y Uso Legítimo (Fair Use)",
      content: `Los metadatos cinematográficos (títulos, sinopsis, fechas, repartos y carátulas promocionales) son indexados mediante conexiones públicas a The Movie Database (TMDb) y material de prensa suministrado con propósitos informativos, de cita y divulgación cultural sin ánimo de lucro perjudicial, amparado en el principio de Fair Use / Uso Legítimo.`
    },
    {
      title: "3. Requisitos para una Notificación Formal de Retirada (Takedown)",
      content: `Si usted es el titular exclusivo de los derechos de autor o actúa con poder notarial o representación legítima de una productora/distribuidora y considera que una ficha, enlace o referencia vulnera sus derechos, puede requerir formalmente su bloqueo o supresión cumpliendo los siguientes requisitos legales mínimos:
      • Identificación clara de la obra protegida supuestamente infringida.
      • Localización exacta del contenido en La Caja LGBT (URL específica o título en disputa).
      • Datos de contacto completos del reclamante (Nombre, Correo corporativo, Empresa titular).
      • Prueba razonable de titularidad o representación legal.
      • Declaración de buena fe de que el uso impugnado no está autorizado por el titular ni por la ley.
      • Declaración bajo pena de perjurio de que la información aportada es veraz y exacta.
      • Firma electrónica o física del titular o apoderado legal.`
    },
    {
      title: "4. Plazos de Respuesta y Procedimiento de Retirada Exprés",
      content: `Tras la radicación formal de una reclamación a través de nuestro Formulario Oficial DMCA o al correo copyright@lacajalgbt.com, nuestro equipo examinará el expediente y procederá, cuando proceda legalmente, a la despublicación o bloqueo del contenido en un plazo máximo de 24 a 48 horas hábiles, notificando el acuse de recibo con un número de ticket de seguimiento oficial.`
    },
    {
      title: "5. Sanción a Reclamaciones Falsas o de Mala Fe",
      content: `Advertimos que conforme a la Sección 512(f) del DMCA y leyes de responsabilidad civil, cualquier persona que declare falsamente y de mala fe que un material infringe derechos de autor puede ser legalmente responsable por los daños y perjuicios económicos causados, incluidos honorarios de abogados.`
    }
  ];

  return (
    <div className="space-y-8" data-testid="legal-content-copyright">
      <div className="border-b border-zinc-800 pb-6">
        <span className="text-xs uppercase tracking-widest text-[#E11D48] font-bold">Propiedad Intelectual</span>
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white mt-1">Aviso de Derechos de Autor y Puerto Seguro (DMCA)</h2>
        <p className="text-zinc-400 text-xs sm:text-sm mt-2 leading-relaxed">
          Procedimiento transparente y garantista de notificación y retirada de contenidos conforme al Digital Millennium Copyright Act y normativas europeas.
        </p>
      </div>

      <div className="space-y-6">
        {points.map((p) => (
          <article key={p.title} className="bg-zinc-900/50 border border-zinc-800/60 p-5 sm:p-6 rounded-xl hover:border-zinc-700 transition-colors">
            <h3 className="text-base sm:text-lg font-semibold text-white mb-2.5 flex items-center gap-2">
              <Scale className="w-4 h-4 text-[#E11D48]" />
              <span>{p.title}</span>
            </h3>
            <div className="text-zinc-300 text-xs sm:text-sm leading-relaxed whitespace-pre-line text-justify sm:text-left">
              {p.content}
            </div>
          </article>
        ))}

        {/* CTA to Interactive Form */}
        <div className="bg-gradient-to-r from-[#E11D48]/20 via-black to-zinc-900 border border-[#E11D48]/40 p-6 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h4 className="text-base font-bold text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span>¿Necesitas radicar una solicitud de retirada formal?</span>
            </h4>
            <p className="text-xs text-zinc-300 mt-1">
              Utiliza nuestro formulario oficial con validación jurídica inmediata y expedición de número de ticket.
            </p>
          </div>
          <Link
            to="/legal/formulario-dmca"
            className="shrink-0 bg-[#E11D48] hover:bg-[#be123c] text-white px-5 py-2.5 rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors shadow-lg shadow-[#E11D48]/30"
          >
            <span>Ir al Formulario DMCA</span>
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// 5. FORMULARIO OFICIAL INTERACTIVO DMCA (DMCA TAKEDOWN FORM)
// -------------------------------------------------------------
function DmcaFormSection({ dmcaForm, setDmcaForm, handleSubmit, submitting, submittedTicket, copyTicket, setSubmittedTicket }) {
  if (submittedTicket) {
    return (
      <div className="bg-emerald-950/30 border border-emerald-500/40 rounded-2xl p-8 text-center space-y-6 animate-in fade-in zoom-in-95 duration-300" data-testid="dmca-success-box">
        <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto border border-emerald-500/40 shadow-lg shadow-emerald-500/20">
          <CheckCircle2 className="w-8 h-8" />
        </div>

        <div>
          <span className="text-xs uppercase tracking-widest text-emerald-400 font-bold">Radicación Oficial Completada</span>
          <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white mt-1">
            Reclamación Registrada con Éxito
          </h2>
          <p className="text-zinc-300 text-sm max-w-xl mx-auto mt-2 leading-relaxed">
            Su requerimiento ha sido registrado formalmente en nuestra base de datos inmutable de cumplimiento legal. Se ha enviado un acuse de recibo a <strong>{dmcaForm.email}</strong>.
          </p>
        </div>

        <div className="bg-black/60 border border-emerald-500/30 p-5 rounded-2xl max-w-md mx-auto flex items-center justify-between gap-4">
          <div className="text-left">
            <p className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">Número de Expediente Legal</p>
            <p className="text-lg font-mono font-bold text-emerald-400">{submittedTicket}</p>
          </div>
          <button
            onClick={copyTicket}
            className="p-2.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 transition-colors flex items-center gap-1 text-xs font-semibold"
            title="Copiar número de expediente"
          >
            <Copy className="w-4 h-4" />
            <span>Copiar</span>
          </button>
        </div>

        <div className="text-xs text-zinc-400 max-w-lg mx-auto bg-zinc-900/60 p-4 rounded-xl border border-zinc-800 text-left space-y-1.5">
          <p className="font-semibold text-zinc-200">Próximos pasos legales:</p>
          <p>• Plazo de respuesta / resolución: <strong>24 a 48 horas hábiles</strong>.</p>
          <p>• Un oficial legal examinará la titularidad y procederá al bloqueo preventivo del contenido señalado.</p>
          <p>• Puede hacer seguimiento en cualquier momento escribiendo a <strong>copyright@lacajalgbt.com</strong> con su número de expediente.</p>
        </div>

        <div className="pt-4 flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={() => {
              setSubmittedTicket(null);
              setDmcaForm({
                full_name: "",
                email: "",
                phone: "",
                organization: "",
                infringing_url_or_title: "",
                original_work_evidence: "",
                description: "",
                good_faith: false,
                accuracy_oath: false,
                signature: "",
              });
            }}
            className="px-5 py-2.5 rounded-xl border border-zinc-700 bg-zinc-900 text-xs font-medium text-zinc-300 hover:bg-zinc-800 transition-colors"
          >
            Radicar otra reclamación
          </button>
          <Link
            to="/"
            className="px-6 py-2.5 rounded-xl bg-[#E11D48] text-white text-xs font-semibold hover:bg-[#be123c] transition-colors"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="legal-content-dmca-form">
      <div className="border-b border-zinc-800 pb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-400 font-bold">
          <AlertTriangle className="w-4 h-4" />
          <span>Procedimiento Formal DMCA / Safe Harbor</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white mt-1">
          Formulario de Notificación de Infracción y Retirada de Contenido
        </h2>
        <p className="text-zinc-400 text-xs sm:text-sm mt-2 leading-relaxed">
          Complete este formulario con valor legal para notificar una presunta infracción de derechos de autor o propiedad intelectual sobre un título o material indexado en La Caja LGBT.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-testid="dmca-claim-form">
        {/* Identificación del Reclamante */}
        <div className="bg-zinc-900/50 border border-zinc-800/80 p-5 sm:p-6 rounded-2xl space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-300 flex items-center gap-2">
            <User className="w-4 h-4 text-[#E11D48]" />
            <span>1. Datos del Titular o Representante Legal</span>
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-zinc-300 mb-1">
                Nombre Completo del Declarador / Apoderado <span className="text-[#E11D48]">*</span>
              </label>
              <input
                type="text"
                data-testid="dmca-input-name"
                required
                value={dmcaForm.full_name}
                onChange={(e) => setDmcaForm({ ...dmcaForm, full_name: e.target.value })}
                placeholder="Ej. María Gómez Rodríguez"
                className="w-full bg-black/60 border border-zinc-700/80 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-zinc-300 mb-1">
                Correo Electrónico Oficial de Contacto <span className="text-[#E11D48]">*</span>
              </label>
              <input
                type="email"
                data-testid="dmca-input-email"
                required
                value={dmcaForm.email}
                onChange={(e) => setDmcaForm({ ...dmcaForm, email: e.target.value })}
                placeholder="legal@productora.com"
                className="w-full bg-black/60 border border-zinc-700/80 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-zinc-300 mb-1">
                Empresa, Productora o Titular de los Derechos
              </label>
              <input
                type="text"
                data-testid="dmca-input-org"
                value={dmcaForm.organization}
                onChange={(e) => setDmcaForm({ ...dmcaForm, organization: e.target.value })}
                placeholder="Ej. Distribuidora Films S.L. / Creador Independiente"
                className="w-full bg-black/60 border border-zinc-700/80 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-zinc-300 mb-1">
                Teléfono de Contacto (Opcional)
              </label>
              <input
                type="tel"
                data-testid="dmca-input-phone"
                value={dmcaForm.phone}
                onChange={(e) => setDmcaForm({ ...dmcaForm, phone: e.target.value })}
                placeholder="+34 910 000 000"
                className="w-full bg-black/60 border border-zinc-700/80 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Identificación de la Obra y Presunta Infracción */}
        <div className="bg-zinc-900/50 border border-zinc-800/80 p-5 sm:p-6 rounded-2xl space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-300 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#E11D48]" />
            <span>2. Identificación del Contenido Impugnado</span>
          </h3>

          <div>
            <label className="block text-xs font-medium text-zinc-300 mb-1">
              URL exacta o Título del contenido en La Caja LGBT <span className="text-[#E11D48]">*</span>
            </label>
            <input
              type="text"
              data-testid="dmca-input-infringing-target"
              required
              value={dmcaForm.infringing_url_or_title}
              onChange={(e) => setDmcaForm({ ...dmcaForm, infringing_url_or_title: e.target.value })}
              placeholder="Ej. https://lacajalgbt.com/titulo/mi-pelicula o Nombre exacto del título"
              className="w-full bg-black/60 border border-zinc-700/80 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-300 mb-1">
              Enlace de la obra original, registro de copyright o prueba de titularidad
            </label>
            <input
              type="text"
              data-testid="dmca-input-evidence"
              value={dmcaForm.original_work_evidence}
              onChange={(e) => setDmcaForm({ ...dmcaForm, original_work_evidence: e.target.value })}
              placeholder="Ej. Enlace a web oficial, registro de propiedad intelectual, IMDb, etc."
              className="w-full bg-black/60 border border-zinc-700/80 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-zinc-300 mb-1">
              Descripción y motivos detallados de la reclamación <span className="text-[#E11D48]">*</span>
            </label>
            <textarea
              rows={4}
              data-testid="dmca-input-description"
              required
              value={dmcaForm.description}
              onChange={(e) => setDmcaForm({ ...dmcaForm, description: e.target.value })}
              placeholder="Explique claramente por qué solicita la retirada o bloqueo del material..."
              className="w-full bg-black/60 border border-zinc-700/80 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
            />
          </div>
        </div>

        {/* Declaraciones Juradas y Firma Digital */}
        <div className="bg-zinc-900/50 border border-zinc-800/80 p-5 sm:p-6 rounded-2xl space-y-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-300 flex items-center gap-2">
            <Scale className="w-4 h-4 text-[#E11D48]" />
            <span>3. Declaraciones Legales y Firma Electrónica</span>
          </h3>

          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="dmca-check-good-faith"
                required
                checked={dmcaForm.good_faith}
                onChange={(e) => setDmcaForm({ ...dmcaForm, good_faith: e.target.checked })}
                className="w-4 h-4 mt-0.5 accent-[#E11D48] rounded shrink-0"
              />
              <span className="text-xs text-zinc-300 leading-relaxed">
                <strong>Declaración de Buena Fe:</strong> Declaro bajo buena fe que el uso del material protegido identificado no está autorizado por el titular de los derechos de autor, su agente o la legislación vigente.
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="dmca-check-accuracy"
                required
                checked={dmcaForm.accuracy_oath}
                onChange={(e) => setDmcaForm({ ...dmcaForm, accuracy_oath: e.target.checked })}
                className="w-4 h-4 mt-0.5 accent-[#E11D48] rounded shrink-0"
              />
              <span className="text-xs text-zinc-300 leading-relaxed">
                <strong>Declaración bajo Pena de Perjurio:</strong> Declaro bajo pena de perjurio que la información contenida en esta notificación es exacta y verídica, y que soy el titular exclusivo de los derechos presuntamente infringidos o estoy facultado legalmente para actuar en su nombre.
              </span>
            </label>
          </div>

          <div className="pt-3 border-t border-zinc-800">
            <label className="block text-xs font-medium text-zinc-300 mb-1">
              Firma Electrónica (Escriba su Nombre y Apellidos completos como firma legal) <span className="text-[#E11D48]">*</span>
            </label>
            <div className="relative">
              <PenTool className="w-4 h-4 text-zinc-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                data-testid="dmca-input-signature"
                required
                value={dmcaForm.signature}
                onChange={(e) => setDmcaForm({ ...dmcaForm, signature: e.target.value })}
                placeholder="/s/ Su Nombre Completo"
                className="w-full bg-black/60 border border-zinc-700/80 rounded-xl pl-10 pr-3.5 py-2.5 text-xs font-mono text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Submit button */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
          <p className="text-[11px] text-zinc-500 leading-tight text-center sm:text-left">
            Al enviar este formulario se generará un número de expediente inmutable con sello de tiempo y dirección IP de radicación.
          </p>
          <button
            type="submit"
            data-testid="dmca-submit-button"
            disabled={submitting}
            className="w-full sm:w-auto bg-[#E11D48] hover:bg-[#be123c] text-white px-8 py-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 shadow-lg shadow-[#E11D48]/20"
          >
            <Send className="w-3.5 h-3.5" />
            <span>{submitting ? "Radicando expediente legal..." : "Radicar Reclamación Oficial DMCA"}</span>
          </button>
        </div>
      </form>
    </div>
  );
}

// -------------------------------------------------------------
// 6. DESCARGO DE RESPONSABILIDAD Y TRANSPARENCIA (DISCLAIMER)
// -------------------------------------------------------------
function DisclaimerContent({ filterText }) {
  const points = [
    {
      title: "1. No Afiliación Institucional",
      content: `La Caja LGBT es un proyecto independiente de catálogo cultural y revista de cine. La inclusión de títulos, marcas comerciales, nombres de festivales o carteles de producciones no implica ninguna relación de patrocinio, respaldo, sociedad o afiliación institucional directa con las grandes compañías distribuidoras cinematográficas, salvo mención expresa en convenios de colaboración cultural.`
    },
    {
      title: "2. Reproductores y Enlaces Externos Incrustados",
      content: `Nuestra plataforma integra reproductores estándar de vídeo que conectan con APIs oficiales de proveedores autorizados (YouTube, Vimeo y distribuidores). La Caja LGBT no tiene control ni ejerce supervisión técnica sobre los servidores de terceros en los que residen los archivos de vídeo, los cuales son gestionados y monetizados por sus respectivos canales oficiales.`
    },
    {
      title: "3. Exactitud de la Información Cinematográfica",
      content: `Aunque realizamos revisiones editoriales continuas para garantizar que las fechas de estreno, sinopsis, repartos y biografías sean precisas, los datos pueden sufrir variaciones imprevistas por parte de las productoras o festivales. La Caja LGBT no garantiza la infalibilidad absoluta de la información de terceros y agradece cualquier sugerencia de corrección a través de nuestro equipo editorial.`
    },
    {
      title: "4. Vocación Social, Diversidad y Visibilidad Cultural",
      content: `El propósito fundamental de La Caja LGBT es servir como punto de encuentro para la difusión de historias de diversidad afectiva, de género y derechos humanos en el ámbito de las artes visuales y la hemeroteca cinematográfica.`
    }
  ];

  return (
    <div className="space-y-8" data-testid="legal-content-disclaimer">
      <div className="border-b border-zinc-800 pb-6">
        <span className="text-xs uppercase tracking-widest text-[#E11D48] font-bold">Transparencia Editorial</span>
        <h2 className="text-2xl sm:text-3xl font-heading font-semibold text-white mt-1">Descargo de Responsabilidad Legal</h2>
        <p className="text-zinc-400 text-xs sm:text-sm mt-2 leading-relaxed">
          Alcance de nuestra actividad informativa, cultural y límites de responsabilidad de la plataforma.
        </p>
      </div>

      <div className="space-y-6">
        {points.map((p) => (
          <article key={p.title} className="bg-zinc-900/50 border border-zinc-800/60 p-5 sm:p-6 rounded-xl hover:border-zinc-700 transition-colors">
            <h3 className="text-base sm:text-lg font-semibold text-white mb-2.5 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span>{p.title}</span>
            </h3>
            <p className="text-zinc-300 text-xs sm:text-sm leading-relaxed text-justify sm:text-left">
              {p.content}
            </p>
          </article>
        ))}
      </div>
    </div>
  );
}
